/**
 * Menu utilisateur avec dropdown
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Menu dropdown pour l'utilisateur connecté avec liens vers les paramètres.
 */

  // Client Component pour Next.js

import { useState, useRef, useEffect } from 'react';

/**
 * Élément de menu
 */
export interface MenuItem {
  /** Libellé affiché */
  label: string;
  /** Icône (emoji ou composant) */
  icon?: string | React.ReactNode;
  /** URL de destination */
  href?: string;
  /** Callback au clic (prioritaire sur href) */
  onClick?: () => void;
  /** Variante (normal ou danger pour déconnexion) */
  variant?: 'normal' | 'danger';
  /** Afficher un séparateur après cet élément */
  separator?: boolean;
}

/**
 * Props du composant UserMenu
 */
export interface UserMenuProps {
  /** Nom de l'utilisateur */
  userName: string;
  /** Email de l'utilisateur */
  userEmail?: string;
  /** Avatar URL ou initiales */
  avatar?: string;
  /** Éléments du menu */
  menuItems: MenuItem[];
  /** Callback de navigation (pour Next.js useRouter ou React Router) */
  onNavigate?: (href: string) => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant UserMenu
 *
 * @example
 * ```tsx
 * import { UserMenu } from '@/modules/auth-caritas';
 * import { useRouter } from 'next/navigation';
 *
 * function Header() {
 *   const router = useRouter();
 *   const { user, logout } = useAuth();
 *
 *   const menuItems = [
 *     { label: 'Mon compte', icon: '👤', href: '/settings/account' },
 *     { label: 'Sécurité & 2FA', icon: '🔒', href: '/settings/2fa' },
 *     { label: 'Mot de passe', icon: '🔑', href: '/settings/password' },
 *     { label: 'Sessions actives', icon: '📱', href: '/settings/sessions' },
 *     { label: 'Historique', icon: '📊', href: '/settings/history', separator: true },
 *     { label: 'Déconnexion', icon: '🚪', onClick: logout, variant: 'danger' },
 *   ];
 *
 *   return (
 *     <UserMenu
 *       userName={user.prenom + ' ' + user.nom}
 *       userEmail={user.email}
 *       menuItems={menuItems}
 *       onNavigate={(href) => router.push(href)}
 *     />
 *   );
 * }
 * ```
 */
export function UserMenu({
  userName,
  userEmail,
  avatar,
  menuItems,
  onNavigate,
  className = '',
}: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * Fermer le menu au clic extérieur
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  /**
   * Gérer le clic sur un élément
   */
  const handleItemClick = (item: MenuItem) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.href && onNavigate) {
      onNavigate(item.href);
    }
    setIsOpen(false);
  };

  /**
   * Obtenir les initiales pour l'avatar
   */
  const getInitials = () => {
    const names = userName.split(' ');
    if (names.length >= 2) {
      return names[0][0] + names[names.length - 1][0];
    }
    return userName.slice(0, 2);
  };

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      {/* Bouton menu utilisateur */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-4 py-2 rounded-lg hover:bg-surface-overlay transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm shadow-md">
          {avatar ? (
            <img src={avatar} alt={userName} className="w-full h-full rounded-full object-cover" />
          ) : (
            getInitials().toUpperCase()
          )}
        </div>

        {/* Infos utilisateur */}
        <div className="hidden sm:block text-left">
          <p className="text-sm font-semibold text-content">
            {userName}
          </p>
          {userEmail && (
            <p className="text-xs text-content-subtle">
              {userEmail}
            </p>
          )}
        </div>

        {/* Icône chevron */}
        <svg
          className={`w-4 h-4 text-content-subtle transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-surface rounded-lg shadow-xl border border-border py-2 z-50 animate-fade-in">
          {/* En-tête du menu */}
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-semibold text-content truncate">
              {userName}
            </p>
            {userEmail && (
              <p className="text-xs text-content-subtle truncate">
                {userEmail}
              </p>
            )}
          </div>

          {/* Éléments du menu */}
          <div className="py-1">
            {menuItems.map((item, index) => (
              <div key={index}>
                <button
                  onClick={() => handleItemClick(item)}
                  className={`w-full flex items-center space-x-3 px-4 py-2.5 text-left transition-colors ${
                    item.variant === 'danger'
                      ? 'text-danger hover:bg-danger-light'
                      : 'text-content-muted hover:bg-surface-overlay'
                  }`}
                >
                  {/* Icône */}
                  {item.icon && (
                    <span className="text-lg flex-shrink-0">
                      {typeof item.icon === 'string' ? item.icon : item.icon}
                    </span>
                  )}

                  {/* Label */}
                  <span className="text-sm font-medium flex-1">
                    {item.label}
                  </span>

                  {/* Flèche pour les liens */}
                  {item.href && !item.onClick && (
                    <svg
                      className="w-4 h-4 text-content-subtle"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  )}
                </button>

                {/* Séparateur */}
                {item.separator && (
                  <div className="my-1 border-t border-border" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
